import { Button } from '@design-system';
import { AnalyticsFlowProvider } from '@utils/analytics/flow';
import { formatArtistNames, renderShopifyArtistName } from '@utils/artists';
import { isInNextSixDays } from '@utils/product';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';

import { PreAuthAuctionButton } from '../../../../features/auction/components/PreAuthAuctionButton';
import { buildAuctionFlowConfig } from '../../../../features/auction/useAuctionFlow';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useFlow } from '../../../../providers';
import { useDraws } from '../../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { CurrencyCode } from '../../../../types/Locations';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import { ProductPrice } from '../../../ProductPrice/ProductPrice';
import { DialogFlowWithStripe } from '../../Dialog/DialogFlowWithStripe';
import ProductDate from '../../ProductDate';
import { MultiProductDrawFlow } from './MultiProductDrawFlow';

export const getOrdinal = (n: number): string => {
  const pr = new Intl.PluralRules('en-UK', { type: 'ordinal' });
  const rule = pr.select(n);

  const suffixes = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
    many: 'th',
    zero: '',
  };

  return suffixes[rule] || suffixes.other;
};

export const AuctionFlow = () => {
  const { t } = useTranslation('product');
  const [showDialog, setShowDialog] = useState(false);
  const { accountAuctionBid, isAccountInDraw, linkedDraws, mainDraw } =
    useDraws();
  const { startFlow } = useFlow();
  const {
    activeDraw,
    customerSelectedFrame,
    entryIntentResponse,
    resetController,
    setupSecret,
  } = usePreAuthDrawCtx();

  const handleFlowClose = useCallback(() => {
    resetController();
  }, [resetController]);

  const drawButtons = useMemo(() => {
    if (!mainDraw) {
      return null;
    }

    if (isAccountInDraw(mainDraw.draw.id)) {
      const flowConfig = buildAuctionFlowConfig(t, mainDraw);
      return (
        <>
          <Button
            disabled
            fullWidth
            icon="ic_check"
            label={t('subscriptionCta.auction.buttonSuccessTitle')}
            size="lg"
            variant="positive"
          />
          <PreAuthAuctionButton
            auctionWithFrames={mainDraw}
            buttonLocation="mainCta"
            hasCheckoutParam={false}
            size="lg"
            startAuctionFlow={() => {
              startFlow({
                ...flowConfig,
                onFlowComplete: handleFlowClose,
              });
            }}
            updateBid
          />
        </>
      );
    }

    if (linkedDraws.length === 0) {
      const flowConfig = buildAuctionFlowConfig(t, mainDraw);

      return (
        <PreAuthAuctionButton
          auctionWithFrames={mainDraw}
          buttonLocation="mainCta"
          hasCheckoutParam={false}
          size="lg"
          startAuctionFlow={() => {
            startFlow({
              ...flowConfig,
              onFlowComplete: handleFlowClose,
            });
          }}
        />
      );
    }

    return (
      <MultiProductDrawFlow
        linkedDraws={linkedDraws}
        mainDraw={mainDraw}
        t={t}
      />
    );
  }, [mainDraw, isAccountInDraw, linkedDraws, t, startFlow, handleFlowClose]);

  const [currency] = useCurrency();

  const { label, launchDate } = useMemo(() => {
    if (!mainDraw?.draw.closeAt) {
      return {
        label: '',
        launchDate: undefined,
      };
    }
    const date = new Date(mainDraw?.draw.closeAt);
    if (isInNextSixDays(date)) {
      return {
        label: 'auction.default.subscriptionDate.closingCountdown',
        launchDate: date,
      };
    }
    return {
      label: 'auction.default.subscriptionDate.closingDate',
      launchDate: date,
    };
  }, [mainDraw?.draw.closeAt]);

  if (!mainDraw) {
    return null;
  }

  const { draw: auction } = mainDraw;

  const learnMoreBodyLabel =
    auction.allocation > 1
      ? 'auction.default.learnMore.RankedAuctionBody'
      : 'auction.default.learnMore.SingleAuctionBody';

  const paragraphs = t(learnMoreBodyLabel, {
    returnObjects: true,
  }) as unknown as string[];

  const accountBid = accountAuctionBid(auction.id);

  return (
    <>
      <div className="flex w-full flex-col gap-6 text-center">
        <div className="flex w-full flex-1 flex-col">
          <p className="text-center font-bold">{t(label)}</p>
          <p className="text-center">
            {launchDate && <ProductDate date={launchDate} />}
          </p>
        </div>
        {auction.auctionReservePrice &&
          auction.auctionReservePrice.amount !== '0' && (
            <div className="flex w-full flex-1 flex-col">
              <p className="text-center text-neutral-4">
                <ProductPrice
                  currencyCode={
                    auction.auctionReservePrice.currencyCode as CurrencyCode
                  }
                  prefix={t('auction.default.reservePrice')}
                  price={Number(auction.auctionReservePrice.amount)}
                  skipConvertCurrency
                  skipTaxCalculation
                />
              </p>
            </div>
          )}

        {accountBid && (
          <div className="flex w-full flex-1 flex-col">
            <p className="text-center text-neutral-6">
              {t('auction.default.accountBid', {
                amount: accountBid.amount,
                currencyCode: accountBid.currencyCode,
              })}
            </p>
          </div>
        )}
        <div className="flex w-full flex-1 flex-col items-center gap-3">
          {drawButtons}
          <button
            className="hyperlink"
            onClick={() => {
              setShowDialog(true);
              // handleHowToClick();
            }}
            type="button"
          >
            {t('auction.default.learnMore.title')}
          </button>
        </div>
      </div>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('auction.default.learnMore.blockChain.title')}
      >
        <div className="flex flex-col gap-3">
          {paragraphs.map((paragraph, index) => (
            <p>
              <Trans
                key={paragraph}
                i18nKey={`auction.default.learnMore.blockChain.body.${index}`}
                values={{
                  size: auction?.allocation,
                  position: `${auction?.allocation}${getOrdinal(
                    auction?.allocation ?? 0,
                  )}`,
                }}
              >
                {paragraph}
              </Trans>
            </p>
          ))}
        </div>
      </SimpleDialog>
      <AnalyticsFlowProvider
        name="draw"
        properties={{
          'type': 'Auction',
          'product title': activeDraw?.product.title,
          'artist name': renderShopifyArtistName(
            activeDraw?.product.artists[0].name ?? '',
          ),
          'product price': activeDraw?.product.price,
          'product can be framed': Boolean(activeDraw?.frames?.length),
          'product frame upgrade available': Boolean(activeDraw?.frames),
          'product frame chosen': Boolean(customerSelectedFrame),
          ecommerce: {
            currency,
            transaction_id: entryIntentResponse.intent?.id,
            value: activeDraw?.product.price ?? '',
            items: [
              {
                item_id: activeDraw?.product.shopifyId,
                item_name: activeDraw?.product.title,
                item_brand: formatArtistNames(
                  activeDraw?.product.artistIds ?? [],
                ),
                item_category: activeDraw?.product.medium,
                price: activeDraw?.product.price,
                quantity: 1,
              },
            ],
          },
        }}
      >
        {() =>
          setupSecret && <DialogFlowWithStripe clientSecret={setupSecret} />
        }
      </AnalyticsFlowProvider>
    </>
  );
};
